// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    Application,
    Auth0Logins,
    CreateApplicationCallbackRequest,
    CreateApplicationRequest,
    CreateSystemRequest,
    CreateTenantRequest,
    GcSystem,
    GrandCentralAccountDetail,
    GrandCentralGetUsersbyGroupPayload,
    GrandCentralUserByGroups,
    GrandCentralUserConsentRequestPayload,
    GrandCentralUserConsentResponse,
    SystemSummary,
    UpdateApplicationCallbackRequest,
    UpdateApplicationConnectionRequest,
    UpdateApplicationRequest,
    UpdateSystemRequest,
} from "./types/cclGrandCentralApiTypes";
import {
    AddEnterpriseIdentity,
    AddRemoveUserGroupRequest,
    AddRemoveUserGroupResponse,
    ApplicationAccount,
    DeleteAuth0AccountRequest,
    DeleteAuth0AccountResponse,
    TenantGroup,
    Invitation,
    RemoveEnterpriseIdentity,
    RemoveEnterpriseIdentityResponse,
    SendCompassInvitationRequest,
    SendInvitationResponse,
    SendInvitationsRequest,
    SendLookingGlassInvitationRequest,
    UpdateExpirationDateReponse,
    UpdateExpirationDateRequest,
    UpdateUserAccountRequest,
    SendEllaInvitationRequest,
    SendEllaInvitationResponse,
    DeleteApplicationUserResponse,
    SendAP2InvitationRequest,
} from "./types/rtkQueryTypes";
import {
    PersonGrandCentralDocument,
    SearchPersonGrandCentralUsersRequest,
} from "./types/search.service.types";
import { EnterpriseUserDetail, UserAuth0Detail } from "./types/userPreferenceTypes";
import {
    ApplicationSearchInvitationBody,
    ApplicationSearchInvitationResponse,
} from "./types/applicationTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

const generateSendInvitationTags = (
    rsp: SendInvitationResponse | undefined,
    error: FetchBaseQueryError | undefined,
    request: SendInvitationsRequest
) => {
    let invalidateTags: { type: any; id: string }[] = [];
    if (rsp !== undefined && error === undefined) {
        invalidateTags = [
            ...request.recipients.map((r) => ({ type: "UserInvitations", id: r.email })),
            ...request.recipients.map((r) => ({ type: "UserApplications", id: r.email })),
        ];
    }
    return invalidateTags;
};

// Define a service using a base URL and expected endpoints
export const cclTokenedGrandCentralApi = createApi({
    reducerPath: "cclTokenedGrandCentralApi",
    tagTypes: [
        "Invitations",
        "AllSystems",
        "AllApplications",
        "UserInvitations",
        "UserApplications",
        "GCAccount",
        "SystemSummary",
        "GCSystem",
        "GCApplication",
        "TenantGroups",
        "UsersByGroup",
        "Auth0Account",
        "UserConsent",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        sendInvitations: builder.mutation<SendInvitationResponse, SendInvitationsRequest>({
            query: (args) => ({
                url: "ccl-access2/GrandCentral/invitations/send",
                method: "POST",
                body: args,
            }),
            async onQueryStarted(request, { dispatch, queryFulfilled }) {
                const rsp = await queryFulfilled;
                setTimeout(() => {
                    dispatch(
                        cclTokenedGrandCentralApi.util.invalidateTags(
                            generateSendInvitationTags(rsp.data, undefined, request)
                        )
                    );
                }, 5000);
            },
        }),
        sendCompassInvitations: builder.mutation<
            SendInvitationResponse,
            SendCompassInvitationRequest
        >({
            query: (args) => ({
                url: "ccl-access2/GrandCentral/invitations/send-compass",
                method: "POST",
                body: args,
            }),
        }),
        sendAp2Invitations: builder.mutation<SendInvitationResponse, SendAP2InvitationRequest>({
            query: (args) => ({
                url: "ccl-access2/GrandCentral/invitations/send-ap2",
                method: "POST",
                body: args,
            }),
        }),
        sendLookingGlassInvitations: builder.mutation<
            SendInvitationResponse,
            SendLookingGlassInvitationRequest
        >({
            query: (args) => ({
                url: "ccl-access2/GrandCentral/invitations/send-looking-glass",
                method: "POST",
                body: args,
            }),
        }),
        sendEllaInvitations: builder.mutation<
            SendEllaInvitationResponse,
            SendEllaInvitationRequest
        >({
            query: (args) => ({
                url: "ccl-access2/GrandCentral/invitations/send-ella",
                method: "POST",
                body: args,
            }),
        }),
        getCclUserInvitations: builder.query<Invitation[], string>({
            query: (email) => `ccl-access2/GrandCentral/invitations/${encodeURIComponent(email)}`,
            providesTags: (_, __, email) => [{ type: "UserInvitations", id: email }],
        }),
        getCclUserApplicationAccounts: builder.query<ApplicationAccount[], string>({
            query: (email) => `ccl-access2/GrandCentral/accounts/${encodeURIComponent(email)}`,
            providesTags: (_, __, email) => [{ type: "UserApplications", id: email }],
        }),
        getCclUserAuth0Detail: builder.query<UserAuth0Detail, string>({
            query: (email) =>
                `ccl-access2/GrandCentral/GetAuth0Detail/${encodeURIComponent(email)}`,
            providesTags: (_, __, email) => [{ type: "Auth0Account", id: email }],
        }),
        getCclUserAccountDetail: builder.query<EnterpriseUserDetail[], string>({
            query: (email) => `ccl-access2/Users/enterprise/by-email/${encodeURIComponent(email)}`,
        }),
        getCclGCUserAccountDetail: builder.query<GrandCentralAccountDetail, string>({
            query: (email) => `ccl-access2/GrandCentral/${encodeURIComponent(email)}`,
            providesTags: (_, __, email) => [{ type: "GCAccount", id: email }],
        }),
        getCclAllSystems: builder.query<GcSystem[], void>({
            query: () => "ccl-access2/GrandCentral/systems",
            providesTags: ["AllSystems"],
        }),
        addCclSystem: builder.mutation<boolean, CreateSystemRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/system`,
                method: "POST",
                body: args,
            }),
            invalidatesTags: ["AllSystems", "AllApplications"],
        }),
        updateCclSystem: builder.mutation<boolean, UpdateSystemRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/system`,
                method: "PUT",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [
                { type: "GCSystem", id: args.systemId },
                "AllSystems",
            ],
        }),
        getCclSystem: builder.query<GcSystem, string>({
            query: (systemId) => `ccl-access2/GrandCentral/system/${systemId}`,
            providesTags: (_, __, systemId) => [{ type: "GCSystem", id: systemId }],
        }),
        getCclAllApplications: builder.query<Application[], void>({
            query: () => `ccl-access2/GrandCentral/applications/all`,
            providesTags: ["AllApplications"],
        }),
        addCclTenant: builder.mutation<boolean, CreateTenantRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/tenant`,
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCSystem", id: args.systemId }],
        }),
        updateCclTenant: builder.mutation<boolean, CreateTenantRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/tenant`,
                method: "PUT",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCSystem", id: args.systemId }],
        }),
        addCclApplication: builder.mutation<boolean, CreateApplicationRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application`,
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCSystem", id: args.systemId }],
        }),
        getCclApplication: builder.query<Application, string>({
            query: (applicationId) => `ccl-access2/GrandCentral/application/${applicationId}`,
            providesTags: (_, __, applicationId) => [{ type: "GCApplication", id: applicationId }],
        }),
        updateCclApplication: builder.mutation<boolean, UpdateApplicationRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application`,
                method: "PUT",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCApplication", id: args.applicationId }],
        }),
        addCclApplicationCallback: builder.mutation<boolean, CreateApplicationCallbackRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/login-application/url`,
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCApplication", id: args.applicationId }],
        }),
        updateCclApplicationCallback: builder.mutation<boolean, UpdateApplicationCallbackRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/login-application/url`,
                method: "PUT",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCApplication", id: args.applicationId }],
        }),
        deleteCclApplicationCallback: builder.mutation<boolean, UpdateApplicationCallbackRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/login-application/url/${args.loginApplicationUrlId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCApplication", id: args.applicationId }],
        }),
        updateCclApplicationConnection: builder.mutation<
            boolean,
            UpdateApplicationConnectionRequest
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/login-application/connection`,
                method: "PUT",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCApplication", id: args.applicationId }],
        }),
        deleteCclApplicationConnection: builder.mutation<
            boolean,
            UpdateApplicationConnectionRequest
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/login-application/connection/${args.loginApplicationConnectionId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCApplication", id: args.applicationId }],
        }),
        getCclApplicationUsers: builder.query<PersonGrandCentralDocument[], string>({
            query: (applicationId) =>
                `ccl-access2/GrandCentral/application/users/${applicationId.toUpperCase()}`,
        }),
        searchCclApplicationUsers: builder.mutation<
            PersonGrandCentralDocument[],
            SearchPersonGrandCentralUsersRequest
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/searchusers/${args.applicationId.toUpperCase()}`,
                method: "POST",
                body: args,
            }),
        }),
        removeIdentity: builder.mutation<
            RemoveEnterpriseIdentityResponse,
            RemoveEnterpriseIdentity
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/accounts/${encodeURIComponent(
                    args.email
                )}/identity/${args.identityId}`,
                method: "DELETE",
                body: args,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCAccount", id: args.email }],
        }),
        addIdentity: builder.mutation<GrandCentralAccountDetail, AddEnterpriseIdentity>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/accounts/${encodeURIComponent(args.email)}/identity`,
                method: "PUT",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args) => [{ type: "GCAccount", id: args.email }],
        }),
        deleteAuth0Account: builder.mutation<DeleteAuth0AccountResponse, DeleteAuth0AccountRequest>(
            {
                query: (args) => ({
                    url: `ccl-access2/GrandCentral/accounts/${encodeURIComponent(
                        args.email
                    )}/auth0/${args.auth0Id}`,
                    method: "DELETE",
                    body: args,
                }),
                invalidatesTags: (_, __, args) => [{ type: "Auth0Account", id: args.email }],
            }
        ),
        enableAccessAppllication: builder.mutation<DeleteAuth0AccountResponse, string[]>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/user/enable`,
                method: "POST",
                body: args,
            }),
        }),
        disableAccessAppllication: builder.mutation<DeleteAuth0AccountResponse, string[]>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/user/disable`,
                method: "POST",
                body: args,
            }),
        }),
        deleteApplicationUser: builder.mutation<DeleteApplicationUserResponse, string>({
            query: (userId) => ({
                url: `ccl-access2/GrandCentral/application/user/${userId}`,
                method: "DELETE",
                body: {},
            }),
        }),
        getAuth0Logins: builder.query<Auth0Logins[], string>({
            query: (userEmail) =>
                `ccl-access2/GrandCentral/GetAuth0Logs/${encodeURIComponent(userEmail)}`,
        }),
        getAuth0ApplicationLogins: builder.query<Auth0Logins[], string>({
            query: (appId) => `ccl-access2/GrandCentral/GetAuth0ApplicationLogs/${appId}`,
        }),
        addUserGroup: builder.mutation<AddRemoveUserGroupResponse, AddRemoveUserGroupRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/user/${args.userId}/group/${args.groupId}`,
                method: "POST",
                body: args,
            }),
        }),
        removeUserGroup: builder.mutation<AddRemoveUserGroupResponse, AddRemoveUserGroupRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/user/${args.userId}/group/${args.groupId}`,
                method: "DELETE",
                body: args,
            }),
        }),
        updateExpirationDate: builder.mutation<
            UpdateExpirationDateReponse,
            UpdateExpirationDateRequest
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/user/${args.userId}/expiration`,
                method: "POST",
                body: args.payload,
            }),
        }),
        getGroupsByTenantId: builder.query<TenantGroup[], string>({
            query: (tenantId) => ({
                url: `ccl-access2/GrandCentral/application/group/${tenantId}`,
                method: "GET",
            }),
            providesTags: (_, __, tenantId) => [{ type: "TenantGroups", id: tenantId }],
        }),
        removeEmail: builder.mutation<boolean, string>({
            query: (email) => ({
                url: `ccl-access2/GrandCentral/accounts/${encodeURIComponent(email)}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, email) => [{ type: "GCAccount", id: email }],
        }),
        getSystemSummary: builder.query<SystemSummary, string>({
            query: (systemId) => ({
                url: `ccl-access2/GrandCentral/system-summary/${systemId}`,
                method: "GET",
            }),
            providesTags: ["SystemSummary"],
        }),
        updateSystemSummary: builder.mutation<boolean, SystemSummary>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/system-summary/update/${args.systemId}`,
                method: "PUT",
                body: args,
            }),
            invalidatesTags: ["SystemSummary"],
        }),
        updateUserAccount: builder.mutation<UpdateExpirationDateReponse, UpdateUserAccountRequest>({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/user/${args.userId}`,
                method: "POST",
                body: args.payload,
            }),
        }),
        searchInvitationsforApplication: builder.mutation<
            ApplicationSearchInvitationResponse[],
            ApplicationSearchInvitationBody
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/searchinvitations/${args.applicationId}`,
                method: "POST",
                body: args.payload,
            }),
        }),
        getUsersbyGroup: builder.query<
            GrandCentralUserByGroups[],
            GrandCentralGetUsersbyGroupPayload
        >({
            query: (args) => ({
                url: `ccl-access2/GrandCentral/application/users/${args.applicationId}/by-groups?groups=${args.groupIds}`,
                method: "GET",
            }),
            providesTags: ["UsersByGroup"],
        }),
        deleteUserConsent: builder.mutation<boolean, GrandCentralUserConsentRequestPayload>({
            query: (request) => ({
                url: "ccl-access2/GrandCentral/consent",
                method: "DELETE",
                body: request,
            }),
        }),
        getUserConsent: builder.mutation<
            GrandCentralUserConsentResponse[],
            GrandCentralUserConsentRequestPayload
        >({
            query: (request) => ({
                url: "ccl-access2/GrandCentral/consent",
                method: "POST",
                body: request,
            }),
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useSendInvitationsMutation,
    useSendCompassInvitationsMutation,
    useSendAp2InvitationsMutation,
    useSendLookingGlassInvitationsMutation,
    useGetCclUserInvitationsQuery,
    useGetCclUserApplicationAccountsQuery,
    useGetCclUserAuth0DetailQuery,
    useGetCclUserAccountDetailQuery,
    useLazyGetCclUserAccountDetailQuery,
    useGetCclGCUserAccountDetailQuery,
    useGetCclAllSystemsQuery,
    useAddCclSystemMutation,
    useUpdateCclSystemMutation,
    useGetCclSystemQuery,
    useAddCclTenantMutation,
    useUpdateCclTenantMutation,
    useGetCclAllApplicationsQuery,
    useAddCclApplicationMutation,
    useGetCclApplicationQuery,
    useUpdateCclApplicationMutation,
    useAddCclApplicationCallbackMutation,
    useUpdateCclApplicationCallbackMutation,
    useDeleteCclApplicationCallbackMutation,
    useRemoveIdentityMutation,
    useAddIdentityMutation,
    useDeleteAuth0AccountMutation,
    useEnableAccessAppllicationMutation,
    useDisableAccessAppllicationMutation,
    useGetAuth0LoginsQuery,
    useDeleteApplicationUserMutation,
    useGetAuth0ApplicationLoginsQuery,
    useLazyGetGroupsByTenantIdQuery,
    useSearchCclApplicationUsersMutation,
    useRemoveEmailMutation,
    useUpdateUserAccountMutation,
    useLazyGetCclUserApplicationAccountsQuery,
    useGetUsersbyGroupQuery,
    useSearchInvitationsforApplicationMutation,
    useGetUserConsentMutation,
    useDeleteUserConsentMutation,
} = cclTokenedGrandCentralApi;
